import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { MaskLines } from "./Reveal";
import { BRAND_BANNER } from "../data/brand";

// Featured film scenes — each cycles through with its Nepali title
const SCENES = [
  {
    url: "https://images.unsplash.com/photo-1585608996900-c8d3a49328a8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    en: "Eternal Flow: The Arrow of Arjun",
    ne: "अर्जुनको बाण",
    slug: "eternal-flow-arrow-of-arjun",
  },
  {
    url: "https://images.unsplash.com/photo-1674033846163-fe3ea31c1f97?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    en: "Threads of Faith: The Budha Subba Story",
    ne: "बुढासुब्बाको कथा",
    slug: "threads-of-faith-budha-subba",
  },
  {
    url: "https://images.unsplash.com/photo-1724154353955-525f6e738c0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    en: "The Buddha — Hidden Truth",
    ne: "बुद्ध – लुकेको सत्य",
    slug: "the-buddha-hidden-truth",
  },
  {
    url: "https://images.unsplash.com/photo-1482424917728-d82d29662023?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    en: "Halesi Dham — Where Mahadev Hides",
    ne: "हलेसी धाम",
    slug: "halesi-dham-mahadev-hides",
  },
  {
    url: "https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&q=85&w=1920",
    en: "Echoes of Mustang",
    ne: "मुस्ताङका प्रतिध्वनिहरू",
    slug: "echoes-of-mustang",
  },
  {
    url: "https://images.unsplash.com/photo-1470770903676-69b98201ea1c?auto=format&fit=crop&q=85&w=1920",
    en: "Purba Ko Ganga",
    ne: "पूर्वको गंगा",
    slug: "purba-ko-ganga",
  },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % SCENES.length), 5500);
    return () => clearInterval(id);
  }, []);

  return (
    <section ref={ref} data-testid="home-hero" className="relative min-h-screen w-full overflow-hidden bg-black">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <AnimatePresence mode="sync">
          <motion.img
            key={idx}
            src={SCENES[idx].url}
            alt={SCENES[idx].en}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.4, ease: [0.7, 0, 0.2, 1] }}
            className="absolute inset-0 w-full h-[115%] object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/60" />
        {/* light-ray gold sweep */}
        <div
          className="absolute inset-0 pointer-events-none mix-blend-screen opacity-40"
          style={{
            background: "radial-gradient(ellipse 60% 40% at 70% 30%, rgba(212,175,55,0.25), transparent 60%)",
          }}
        />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-24 min-h-screen flex flex-col justify-between">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 1 }}
          className="flex items-center gap-4"
        >
          <span className="w-10 h-px bg-[color:var(--dk-gold)]" />
          <span className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)]">
            Documentaries · Private Events · Festivals · Social Awareness
          </span>
        </motion.div>

        <div className="mt-auto">
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.25, ease: [0.7, 0, 0.2, 1] }}
            className="mb-10 md:mb-14"
            data-testid="hero-brand-banner"
          >
            <img
              src={BRAND_BANNER}
              alt="धाराकला · Dhara Kala Pvt Ltd Presents"
              className="max-w-full md:max-w-2xl lg:max-w-3xl h-auto drop-shadow-[0_10px_60px_rgba(212,175,55,0.35)]"
            />
          </motion.div>
          <h1 className="font-serif-display font-light text-white leading-[0.95] text-[9vw] md:text-[6.4vw] lg:text-[5.6rem] xl:text-[6.8rem] tracking-tighter max-w-6xl">
            <MaskLines lines={["Stories Rooted in Culture."]} delay={0.35} testid="hero-title-1" />
            <span className="block italic text-[color:var(--dk-gold)] font-serif-display">
              <MaskLines lines={["Cinema Made for the World."]} delay={0.55} testid="hero-title-2" />
            </span>
          </h1>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
              className="md:col-span-6 font-serif-display italic text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed"
            >
              Dhara Kala Movies brings history, spirituality, heritage and human stories
              to life through meaningful cinematic experiences.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.9 }}
              className="md:col-span-6 md:col-start-7 flex flex-wrap items-center gap-4 md:justify-end"
            >
              <Link data-testid="hero-cta-projects" to="/films"
                className="group inline-flex items-center gap-3 bg-[color:var(--dk-red)] hover:bg-[color:var(--dk-red-deep)] text-white px-7 py-4 text-[11px] uppercase tracking-overline transition-colors">
                <Play size={14} className="fill-white" />
                Explore Our Projects
                <ArrowUpRight size={16} className="ml-2 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <Link data-testid="hero-cta-contact" to="/contact"
                className="inline-flex items-center gap-3 border border-[color:var(--dk-gold)]/60 text-[color:var(--dk-gold)] hover:bg-[color:var(--dk-gold)] hover:text-black px-7 py-4 text-[11px] uppercase tracking-overline transition-colors">
                Contact the Studio
              </Link>
            </motion.div>
          </div>

          {/* Featured film indicator with Nepali title */}
          <div className="mt-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6" data-testid="hero-scene-strip">
            <div className="flex items-center gap-6 text-[10px] uppercase tracking-overline text-white/60">
              <div className="flex gap-2">
                {SCENES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    aria-label={`Featured film ${i + 1}`}
                    className={`h-px w-8 transition-colors ${i === idx ? "bg-[color:var(--dk-gold)]" : "bg-white/30 hover:bg-white/60"}`}
                  />
                ))}
              </div>
              <span className="text-white/50">Now Featuring · {String(idx + 1).padStart(2, "0")} / {String(SCENES.length).padStart(2, "0")}</span>
            </div>
            <div className="text-left md:text-right" data-testid="hero-featured-title">
              <p className="font-serif-display italic text-[color:var(--dk-gold)] text-2xl md:text-3xl leading-tight">
                {SCENES[idx].ne}
              </p>
              <p className="text-[10px] uppercase tracking-overline text-white/70 mt-1">
                {SCENES[idx].en}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[10px] uppercase tracking-overline text-white/50 flex flex-col items-center gap-3"
      >
        <span>Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="block w-px h-8 bg-white/40" />
      </motion.div>
    </section>
  );
}
